import { useRef, useEffect, useState, useMemo, useCallback } from 'react';
import type { NFTLayerAsset, BannerCropNFTElement } from '../../types';
import { preloadLayerImages, getDecodedImageFromCache, type SnapGuides } from '../../hooks';
import { isBackgroundLayer, isFrameLayer } from '../../utils/layerUtils';

interface BannerCropCanvasProps {
  width: number;
  height: number;
  imageUrl?: string; // Legacy single image mode
  layerAssets?: NFTLayerAsset[]; // New layer-based mode
  offset: { x: number; y: number };
  scale: number;
  onImageLoad?: (width: number, height: number) => void;
  fixedLayerIds?: Set<string>; // User-toggled fixed layer IDs (overrides auto-detection)
  viewportWidth?: number; // Display width (for scaling down large banners)
  viewportHeight?: number; // Display height
  backgroundUrl?: string | null; // Background image URL
  backgroundVerticalOffset?: number; // Vertical offset for background (-100 to 100, percentage of overflow)
  frameVerticalOffset?: number; // Vertical offset for frame layers (-100 to 100, percentage of overflow)
  snapGuides?: SnapGuides; // Snap alignment guides to display
  isDragging?: boolean; // Whether currently dragging the main NFT
  additionalNFTs?: BannerCropNFTElement[]; // Additional NFT characters (character layers only)
  selectedNFTId?: string | null; // ID of the selected additional NFT (for showing selection indicator)
  onDragAdditionalNFT?: (id: string, offset: { x: number; y: number }) => void; // Callback when additional NFT is dragged
  onSelectNFT?: (id: string | null) => void; // Callback when an NFT is clicked to select it
  snapEnabled?: boolean; // Enable snap to center for additional NFTs
  snapThreshold?: number; // Pixels from center to trigger snap
}

// Single source of truth lives in utils/layerUtils (checks id AND type).
// Re-exported here so existing `import { isFillLayerByDefault } from './BannerCropCanvas'`
// call sites keep working.
import { isFillLayerByDefault } from '../../utils/layerUtils';
export { isFillLayerByDefault };

export function BannerCropCanvas({
  width,
  height,
  imageUrl,
  layerAssets = [],
  offset,
  scale,
  onImageLoad,
  fixedLayerIds,
  viewportWidth,
  viewportHeight,
  backgroundUrl,
  backgroundVerticalOffset = 0,
  frameVerticalOffset = 0,
  snapGuides,
  isDragging = false,
  additionalNFTs = [],
  selectedNFTId,
  onDragAdditionalNFT,
  onSelectNFT,
  snapEnabled = true,
  snapThreshold = 12,
}: BannerCropCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [layersLoaded, setLayersLoaded] = useState(false);
  const [backgroundImage, setBackgroundImage] = useState<HTMLImageElement | null>(null);
  const [backgroundLoaded, setBackgroundLoaded] = useState(false);
  const [additionalNFTsLoaded, setAdditionalNFTsLoaded] = useState(false);

  // Drag state for additional NFTs
  const [isDraggingAdditional, setIsDraggingAdditional] = useState(false);
  const [draggedNFTId, setDraggedNFTId] = useState<string | null>(null);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [dragStartOffset, setDragStartOffset] = useState({ x: 0, y: 0 });
  const [hoveredNFTId, setHoveredNFTId] = useState<string | null>(null);
  // Snap guides state for additional NFT dragging
  const [additionalSnapGuides, setAdditionalSnapGuides] = useState<{ horizontal: boolean; vertical: boolean }>({ horizontal: false, vertical: false });

  // Use layer-based mode if layerAssets are provided and not empty
  const useLayerMode = layerAssets.length > 0;

  // Calculate display dimensions
  const displayWidth = viewportWidth || width;
  const displayHeight = viewportHeight || height;

  // Scale factor from viewport to canvas (for converting offset)
  const scaleFactorX = width / displayWidth;
  const scaleFactorY = height / displayHeight;

  // Build render layers with fixed status
  const renderLayers = useMemo(() => {
    return layerAssets.map((asset, index) => ({
      asset,
      order: index,
      isFixed: fixedLayerIds ? fixedLayerIds.has(asset.id) : isFillLayerByDefault(asset),
    }));
  }, [layerAssets, fixedLayerIds]);

  // Calculate bounds for an additional NFT (in viewport coordinates)
  const getNFTBounds = useCallback(
    (nft: BannerCropNFTElement) => {
      if (nft.characterOnlyLayers.length === 0) return null;

      const firstLayer = nft.characterOnlyLayers[0];
      const img = getDecodedImageFromCache(firstLayer.uri);
      if (!img) return null;

      const imageAspect = img.width / img.height;
      const baseHeight = displayHeight;
      const baseWidth = baseHeight * imageAspect;
      const drawWidth = baseWidth * nft.scale;
      const drawHeight = baseHeight * nft.scale;
      const drawX = (displayWidth - drawWidth) / 2 + nft.offset.x;
      const drawY = (displayHeight - drawHeight) / 2 + nft.offset.y;

      return { x: drawX, y: drawY, width: drawWidth, height: drawHeight };
    },
    [displayWidth, displayHeight]
  );

  // Helper to apply snap-to-center for additional NFT dragging
  const applySnapToOffset = useCallback(
    (rawOffset: { x: number; y: number }): { offset: { x: number; y: number }; snappedH: boolean; snappedV: boolean } => {
      let snappedX = rawOffset.x;
      let snappedY = rawOffset.y;
      let snappedH = false;
      let snappedV = false;

      if (snapEnabled) {
        // Snap to horizontal center (X near 0)
        if (Math.abs(rawOffset.x) < snapThreshold) {
          snappedX = 0;
          snappedH = true;
        }
        // Snap to vertical center (Y near 0)
        if (Math.abs(rawOffset.y) < snapThreshold) {
          snappedY = 0;
          snappedV = true;
        }
      }

      return { offset: { x: snappedX, y: snappedY }, snappedH, snappedV };
    },
    [snapEnabled, snapThreshold]
  );

  // Get position from mouse or touch event
  const getEventPos = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if ('touches' in e) {
      const touch = e.touches[0] || e.changedTouches[0];
      return { x: touch.clientX, y: touch.clientY };
    }
    return { x: (e as React.MouseEvent).clientX, y: (e as React.MouseEvent).clientY };
  }, []);

  // Get position relative to canvas
  const getCanvasPos = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const pos = getEventPos(e);
    return { x: pos.x - rect.left, y: pos.y - rect.top };
  }, [getEventPos]);

  // Mouse/Touch event handlers for additional NFT interaction
  // Click-to-select: clicking on any NFT selects it and starts dragging
  const handleStart = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
      // Prevent default to stop scrolling on touch devices
      if ('touches' in e) {
        e.preventDefault();
      }

      const canvas = canvasRef.current;
      if (!canvas) return;

      const canvasPos = getCanvasPos(e);
      const eventPos = getEventPos(e);

      // Check additional NFTs first (they render on top)
      // Iterate in reverse order to check top-most (last rendered) first
      for (let i = additionalNFTs.length - 1; i >= 0; i--) {
        const nft = additionalNFTs[i];
        const bounds = getNFTBounds(nft);
        if (!bounds) continue;

        if (canvasPos.x >= bounds.x && canvasPos.x <= bounds.x + bounds.width && 
            canvasPos.y >= bounds.y && canvasPos.y <= bounds.y + bounds.height) {
          // Stop propagation for mouse events
          if (!('touches' in e)) {
            e.stopPropagation();
          }
          // Select this NFT
          onSelectNFT?.(nft.id);
          // Start dragging immediately
          setIsDraggingAdditional(true);
          setDraggedNFTId(nft.id);
          setDragStart({ x: eventPos.x, y: eventPos.y });
          setDragStartOffset({ ...nft.offset });
          return;
        }
      }

      // If no additional NFT was clicked, select primary (let parent handle primary drag)
      onSelectNFT?.('primary');
    },
    [additionalNFTs, getNFTBounds, onSelectNFT, getCanvasPos, getEventPos]
  );

  // Legacy mouse handler
  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      handleStart(e);
    },
    [handleStart]
  );

  // Touch handler
  const handleTouchStart = useCallback(
    (e: React.TouchEvent<HTMLCanvasElement>) => {
      handleStart(e);
    },
    [handleStart]
  );

  const handleMove = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
      // Prevent default to stop scrolling on touch devices when dragging
      if ('touches' in e && isDraggingAdditional) {
        e.preventDefault();
      }

      const eventPos = getEventPos(e);

      // Handle dragging
      if (isDraggingAdditional && draggedNFTId) {
        const deltaX = eventPos.x - dragStart.x;
        const deltaY = eventPos.y - dragStart.y;

        const rawOffset = {
          x: dragStartOffset.x + deltaX,
          y: dragStartOffset.y + deltaY,
        };

        // Apply snapping
        const { offset: snappedOffset, snappedH, snappedV } = applySnapToOffset(rawOffset);
        setAdditionalSnapGuides({ horizontal: snappedH, vertical: snappedV });

        onDragAdditionalNFT?.(draggedNFTId, snappedOffset);
        return;
      }

      // Handle hover detection for cursor change - show grab cursor on any additional NFT
      // Only for mouse events (not touch)
      if (!('touches' in e)) {
        const canvas = canvasRef.current;
        if (canvas) {
          const rect = canvas.getBoundingClientRect();
          const x = eventPos.x - rect.left;
          const y = eventPos.y - rect.top;

          // Check all additional NFTs for hover (reverse order for z-index)
          for (let i = additionalNFTs.length - 1; i >= 0; i--) {
            const nft = additionalNFTs[i];
            const bounds = getNFTBounds(nft);
            if (bounds && x >= bounds.x && x <= bounds.x + bounds.width && y >= bounds.y && y <= bounds.y + bounds.height) {
              setHoveredNFTId(nft.id);
              return;
            }
          }
        }
        setHoveredNFTId(null);
      }
    },
    [isDraggingAdditional, draggedNFTId, dragStart, dragStartOffset, onDragAdditionalNFT, applySnapToOffset, additionalNFTs, getNFTBounds, getEventPos]
  );

  // Legacy mouse handler
  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      handleMove(e);
    },
    [handleMove]
  );

  // Touch handler
  const handleTouchMove = useCallback(
    (e: React.TouchEvent<HTMLCanvasElement>) => {
      handleMove(e);
    },
    [handleMove]
  );

  const handleEnd = useCallback(() => {
    setIsDraggingAdditional(false);
    setDraggedNFTId(null);
    setAdditionalSnapGuides({ horizontal: false, vertical: false });
  }, []);

  // Legacy mouse handlers
  const handleMouseUp = useCallback(() => {
    handleEnd();
  }, [handleEnd]);

  const handleMouseLeave = useCallback(() => {
    // Don't stop dragging on mouse leave - let global handler manage it
    // But clear hover state
    setHoveredNFTId(null);
  }, []);

  // Touch handler
  const handleTouchEnd = useCallback(() => {
    handleEnd();
  }, [handleEnd]);

  const handleTouchCancel = useCallback(() => {
    handleEnd();
  }, [handleEnd]);

  // Global event handlers for drag outside canvas (mouse and touch)
  useEffect(() => {
    if (!isDraggingAdditional) return;

    // Mouse handlers
    const handleGlobalMouseMove = (e: MouseEvent) => {
      if (!isDraggingAdditional || !draggedNFTId) return;

      const deltaX = e.clientX - dragStart.x;
      const deltaY = e.clientY - dragStart.y;

      const rawOffset = {
        x: dragStartOffset.x + deltaX,
        y: dragStartOffset.y + deltaY,
      };

      // Apply snapping inline (same logic as applySnapToOffset)
      let snappedX = rawOffset.x;
      let snappedY = rawOffset.y;
      let snappedH = false;
      let snappedV = false;

      if (snapEnabled) {
        if (Math.abs(rawOffset.x) < snapThreshold) {
          snappedX = 0;
          snappedH = true;
        }
        if (Math.abs(rawOffset.y) < snapThreshold) {
          snappedY = 0;
          snappedV = true;
        }
      }

      setAdditionalSnapGuides({ horizontal: snappedH, vertical: snappedV });
      onDragAdditionalNFT?.(draggedNFTId, { x: snappedX, y: snappedY });
    };

    const handleGlobalMouseUp = () => {
      setIsDraggingAdditional(false);
      setDraggedNFTId(null);
      setAdditionalSnapGuides({ horizontal: false, vertical: false });
    };

    // Touch handlers
    const handleGlobalTouchMove = (e: TouchEvent) => {
      if (!isDraggingAdditional || !draggedNFTId) return;

      // Prevent scrolling while dragging
      e.preventDefault();

      const touch = e.touches[0];
      const deltaX = touch.clientX - dragStart.x;
      const deltaY = touch.clientY - dragStart.y;

      const rawOffset = {
        x: dragStartOffset.x + deltaX,
        y: dragStartOffset.y + deltaY,
      };

      // Apply snapping inline (same logic as applySnapToOffset)
      let snappedX = rawOffset.x;
      let snappedY = rawOffset.y;
      let snappedH = false;
      let snappedV = false;

      if (snapEnabled) {
        if (Math.abs(rawOffset.x) < snapThreshold) {
          snappedX = 0;
          snappedH = true;
        }
        if (Math.abs(rawOffset.y) < snapThreshold) {
          snappedY = 0;
          snappedV = true;
        }
      }

      setAdditionalSnapGuides({ horizontal: snappedH, vertical: snappedV });
      onDragAdditionalNFT?.(draggedNFTId, { x: snappedX, y: snappedY });
    };

    const handleGlobalTouchEnd = () => {
      setIsDraggingAdditional(false);
      setDraggedNFTId(null);
      setAdditionalSnapGuides({ horizontal: false, vertical: false });
    };

    window.addEventListener('mousemove', handleGlobalMouseMove);
    window.addEventListener('mouseup', handleGlobalMouseUp);
    window.addEventListener('touchmove', handleGlobalTouchMove, { passive: false });
    window.addEventListener('touchend', handleGlobalTouchEnd);
    window.addEventListener('touchcancel', handleGlobalTouchEnd);

    return () => {
      window.removeEventListener('mousemove', handleGlobalMouseMove);
      window.removeEventListener('mouseup', handleGlobalMouseUp);
      window.removeEventListener('touchmove', handleGlobalTouchMove);
      window.removeEventListener('touchend', handleGlobalTouchEnd);
      window.removeEventListener('touchcancel', handleGlobalTouchEnd);
    };
  }, [isDraggingAdditional, draggedNFTId, dragStart, dragStartOffset, onDragAdditionalNFT, snapEnabled, snapThreshold]);

  // Preload layer images when layerAssets change
  useEffect(() => {
    if (useLayerMode && layerAssets.length > 0) {
      setLayersLoaded(false);
      preloadLayerImages(layerAssets).then(() => {
        setLayersLoaded(true);
      });
    } else {
      setLayersLoaded(true);
    }
  }, [layerAssets, useLayerMode]);

  // Preload additional NFT layers when they change
  useEffect(() => {
    if (additionalNFTs.length === 0) {
      setAdditionalNFTsLoaded(true);
      return;
    }

    setAdditionalNFTsLoaded(false);
    const allLayers = additionalNFTs.flatMap(nft => nft.characterOnlyLayers);
    if (allLayers.length > 0) {
      preloadLayerImages(allLayers).then(() => {
        setAdditionalNFTsLoaded(true);
      });
    } else {
      setAdditionalNFTsLoaded(true);
    }
  }, [additionalNFTs]);

  // Load background image when backgroundUrl changes
  useEffect(() => {
    if (!backgroundUrl) {
      setBackgroundImage(null);
      setBackgroundLoaded(true);
      return;
    }

    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      setBackgroundImage(img);
      setBackgroundLoaded(true);
    };

    img.onerror = () => {
      console.error('Failed to load background image:', backgroundUrl);
      setBackgroundImage(null);
      setBackgroundLoaded(true);
    };

    setBackgroundLoaded(false);
    img.src = backgroundUrl;

    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [backgroundUrl]);

  // Load single image when URL changes (legacy mode)
  useEffect(() => {
    if (useLayerMode || !imageUrl) return;

    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      setImage(img);
      setImageLoaded(true);
      if (onImageLoad) {
        onImageLoad(img.width, img.height);
      }
    };

    img.onerror = () => {
      console.error('Failed to load image:', imageUrl);
      setImageLoaded(false);
    };

    img.src = imageUrl;

    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [imageUrl, onImageLoad, useLayerMode]);

  // Draw on canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Check if we have content to draw
    if (useLayerMode && !layersLoaded) return;
    if (!useLayerMode && (!imageLoaded || !image)) return;
    if (!backgroundLoaded) return;
    if (!additionalNFTsLoaded) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw background (either image or checkerboard pattern for transparency)
    if (backgroundImage) {
      // Draw background image with cover mode
      const imageAspect = backgroundImage.width / backgroundImage.height;
      const canvasAspect = canvas.width / canvas.height;

      let drawWidth: number;
      let drawHeight: number;

      if (imageAspect > canvasAspect) {
        drawHeight = canvas.height;
        drawWidth = drawHeight * imageAspect;
      } else {
        drawWidth = canvas.width;
        drawHeight = drawWidth / imageAspect;
      }

      const drawX = (canvas.width - drawWidth) / 2;
      // Apply vertical offset as percentage of overflow
      const verticalOverflow = drawHeight - canvas.height;
      const yOffset = (backgroundVerticalOffset / 100) * (verticalOverflow / 2);
      const drawY = (canvas.height - drawHeight) / 2 + yOffset;

      ctx.drawImage(backgroundImage, drawX, drawY, drawWidth, drawHeight);
    } else {
      // Draw checkerboard pattern for transparency
      const squareSize = 20;
      for (let y = 0; y < canvas.height; y += squareSize) {
        for (let x = 0; x < canvas.width; x += squareSize) {
          const isLight = ((x + y) / squareSize) % 2 === 0;
          ctx.fillStyle = isLight ? '#2a2a3e' : '#1f1f2e';
          ctx.fillRect(x, y, squareSize, squareSize);
        }
      }
    }

    if (useLayerMode) {
      // Layer-based rendering
      let primaryImageReported = false;

      for (const renderLayer of renderLayers) {
        const img = getDecodedImageFromCache(renderLayer.asset.uri);
        if (img) {
          ctx.save();

          // Disable antialiasing for crisp pixel art rendering
          ctx.imageSmoothingEnabled = false;

          if (renderLayer.isFixed) {
            // Skip NFT's own background/frame layers if a custom background is selected
            // This allows the user-selected background to show instead
            if (backgroundImage) {
              ctx.restore();
              continue;
            }

            // Fill layers: ALWAYS cover the entire canvas
            // They do NOT scale with user zoom - they always fill completely
            const imageAspect = img.width / img.height;
            const canvasAspect = canvas.width / canvas.height;

            let drawWidth: number;
            let drawHeight: number;

            if (imageAspect > canvasAspect) {
              // Image is wider - fit to height (cover mode)
              drawHeight = canvas.height;
              drawWidth = drawHeight * imageAspect;
            } else {
              // Image is taller or same - fit to width (cover mode)
              drawWidth = canvas.width;
              drawHeight = drawWidth / imageAspect;
            }

            // Determine if this is a frame layer or background layer for vertical offset
            const verticalOffsetPercent = isFrameLayer(renderLayer.asset)
              ? frameVerticalOffset
              : isBackgroundLayer(renderLayer.asset)
                ? backgroundVerticalOffset
                : 0;

            // Apply vertical offset as percentage of overflow
            const verticalOverflow = drawHeight - canvas.height;
            const yOffset = (verticalOffsetPercent / 100) * (verticalOverflow / 2);

            const drawX = (canvas.width - drawWidth) / 2;
            const drawY = (canvas.height - drawHeight) / 2 + yOffset;

            ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
          } else {
            // Standard/character layers: CONTAIN within height
            // This ensures the character's feet fit inside the canvas height
            // Report the first standard layer's dimensions for bounds calculation
            if (!primaryImageReported && onImageLoad) {
              onImageLoad(img.width, img.height);
              primaryImageReported = true;
            }

            const imageAspect = img.width / img.height;

            // Base size: fit within canvas height (contain-height mode)
            // This ensures the character fits fully within the vertical space
            let baseHeight = canvas.height;
            let baseWidth = baseHeight * imageAspect;

            // Apply user scale on top of the contain-height base
            const drawWidth = baseWidth * scale;
            const drawHeight = baseHeight * scale;

            // Center on canvas and apply offset (scaled from viewport to canvas coords)
            const drawX = (canvas.width - drawWidth) / 2 + offset.x * scaleFactorX;
            const drawY = (canvas.height - drawHeight) / 2 + offset.y * scaleFactorY;

            ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
          }

          ctx.restore();
        }
      }
    } else {
      // Legacy single image rendering with contain-height mode
      ctx.save();

      // Disable antialiasing for crisp pixel art rendering
      ctx.imageSmoothingEnabled = false;

      const imageAspect = image!.width / image!.height;

      // Contain-height mode: fit within canvas height
      let baseHeight = canvas.height;
      let baseWidth = baseHeight * imageAspect;

      // Apply scale
      const drawWidth = baseWidth * scale;
      const drawHeight = baseHeight * scale;

      // Center on canvas and apply offset (scaled from viewport to canvas coords)
      const drawX = (canvas.width - drawWidth) / 2 + offset.x * scaleFactorX;
      const drawY = (canvas.height - drawHeight) / 2 + offset.y * scaleFactorY;

      ctx.drawImage(image!, drawX, drawY, drawWidth, drawHeight);

      ctx.restore();
    }

    // Draw additional NFT characters (character layers only, no backgrounds/frames)
    for (const additionalNFT of additionalNFTs) {
      for (const layer of additionalNFT.characterOnlyLayers) {
        const img = getDecodedImageFromCache(layer.uri);
        if (!img) continue;

        ctx.save();
        ctx.imageSmoothingEnabled = false;

        const imageAspect = img.width / img.height;

        // Base size: fit within canvas height (contain-height mode)
        let baseHeight = canvas.height;
        let baseWidth = baseHeight * imageAspect;

        // Apply the NFT's individual scale
        const drawWidth = baseWidth * additionalNFT.scale;
        const drawHeight = baseHeight * additionalNFT.scale;

        // Center on canvas and apply this NFT's individual offset
        const drawX = (canvas.width - drawWidth) / 2 + additionalNFT.offset.x * scaleFactorX;
        const drawY = (canvas.height - drawHeight) / 2 + additionalNFT.offset.y * scaleFactorY;

        ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
        ctx.restore();
      }

      // Draw selection indicator if this NFT is selected
      if (selectedNFTId === additionalNFT.id && additionalNFT.characterOnlyLayers.length > 0) {
        const firstLayer = additionalNFT.characterOnlyLayers[0];
        const img = getDecodedImageFromCache(firstLayer.uri);
        if (img) {
          const imageAspect = img.width / img.height;
          let baseHeight = canvas.height;
          let baseWidth = baseHeight * imageAspect;
          const drawWidth = baseWidth * additionalNFT.scale;
          const drawHeight = baseHeight * additionalNFT.scale;
          const drawX = (canvas.width - drawWidth) / 2 + additionalNFT.offset.x * scaleFactorX;
          const drawY = (canvas.height - drawHeight) / 2 + additionalNFT.offset.y * scaleFactorY;

          ctx.save();
          ctx.strokeStyle = '#60a5fa';
          ctx.lineWidth = 3;
          ctx.setLineDash([8, 4]);
          ctx.strokeRect(drawX, drawY, drawWidth, drawHeight);
          ctx.restore();
        }
      }
    }

    // Draw snap alignment guides when dragging (primary NFT or additional NFTs)
    const showPrimaryGuides = isDragging && snapGuides && (snapGuides.horizontal || snapGuides.vertical);
    const showAdditionalGuides = isDraggingAdditional && (additionalSnapGuides.horizontal || additionalSnapGuides.vertical);

    if (showPrimaryGuides || showAdditionalGuides) {
      ctx.save();
      ctx.strokeStyle = '#f472b6'; // Pink color for snap guides
      ctx.lineWidth = 2;
      ctx.setLineDash([8, 4]);

      // Determine which guides to show
      const showH = (showPrimaryGuides && snapGuides?.horizontal) || (showAdditionalGuides && additionalSnapGuides.horizontal);
      const showV = (showPrimaryGuides && snapGuides?.vertical) || (showAdditionalGuides && additionalSnapGuides.vertical);

      // Vertical center line (for horizontal centering)
      if (showH) {
        ctx.beginPath();
        ctx.moveTo(canvas.width / 2, 0);
        ctx.lineTo(canvas.width / 2, canvas.height);
        ctx.stroke();
      }

      // Horizontal center line (for vertical centering)
      if (showV) {
        ctx.beginPath();
        ctx.moveTo(0, canvas.height / 2);
        ctx.lineTo(canvas.width, canvas.height / 2);
        ctx.stroke();
      }

      ctx.restore();
    }
  }, [image, imageLoaded, layersLoaded, useLayerMode, renderLayers, width, height, offset, scale, onImageLoad, backgroundImage, backgroundLoaded, backgroundVerticalOffset, frameVerticalOffset, snapGuides, isDragging, scaleFactorX, scaleFactorY, additionalNFTs, additionalNFTsLoaded, selectedNFTId, isDraggingAdditional, additionalSnapGuides]);

  // Determine cursor based on state
  const cursorClass = isDraggingAdditional
    ? 'cursor-grabbing'
    : hoveredNFTId
    ? 'cursor-grab'
    : '';

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      className={`rounded-lg border border-surface-border shadow-lg ${cursorClass} touch-none`}
      style={{ width: displayWidth, height: displayHeight, touchAction: 'none' }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchCancel}
    />
  );
}
